/**
 * EDITOR DI CAMPO DEL BUILDER — i controlli con cui si sceglie un campo del catalogo e se ne compone
 * il valore.
 *
 * Vivevano dentro `AutomationForm`, dove erano nati per il blocco SE. Ne sono usciti quando il
 * blocco ALLORA ha avuto bisogno degli stessi: la configurazione dell'azione "aggiorna campo" chiede
 * un campo e un valore, cioè esattamente una riga condizione senza l'operatore, e doveva avere la
 * stessa resa — non una somiglianza ottenuta con altri componenti.
 *
 * Sono `<button>` e `<input>` VERI, non componenti MUI con trigger custom: è la ragione per cui il
 * `<fieldset disabled>` che il builder usa per la sola lettura li spegne da sé, senza `disabled`
 * espliciti da ricordare a ogni chiamata. Una `Select` di MUI non l'avrebbe fatto — mostra un `div`
 * e tiene l'input nascosto — ed è il motivo per cui il form di configurazione, che quella Select la
 * usava, aveva bisogno di rattoppi.
 */

import { useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Checkbox from '@mui/material/Checkbox';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import { parseJsonArray } from './automationMappers';
import { DEFAULT_RELATIVE_UNIT, RELATIVE_UNITS } from './automationTypes';
import type { CatalogField, OperatorCardinality } from './automationTypes';

/** Opzione di una combo del builder: valore tecnico + etichetta localizzata (dal catalogo). */
export interface ChipOption {
  value: string;
  label: string;
}

/**
 * Soglia di opzioni sopra la quale la tendina mostra un campo di ricerca. Sotto, una lista corta
 * si legge in un colpo d'occhio e un filtro sarebbe rumore; sopra, vocabolari come le nazioni del
 * portale (circa 249) o le regioni (un centinaio) diventano scomodi da scorrere a mano. Guarda il
 * numero di opzioni ricevute a runtime, non il tipo di campo: nessun descrittore dichiara quando un
 * vocabolario è grande.
 */
const SEARCH_THRESHOLD = 10;

/**
 * Normalizza una label per il confronto di ricerca: minuscole e senza segni diacritici, così
 * "citta" trova "Città" e "trento" trova "Trento". Unica implementazione, usata da entrambe le
 * combo: due normalizzazioni della stessa regola divergerebbero.
 */
function normalizeForSearch(label: string): string {
  return label
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Tasti che l'input di ricerca lascia salire al Menu — tutti gli altri restano confinati
 * nell'input. Escape deve poterlo chiudere da tastiera: senza, un menu che non si chiude più con
 * la tastiera è un difetto di accessibilità, non una scomodità. Le frecce verticali devono salire
 * perché, dopo aver filtrato, sono l'unico modo da tastiera di scendere nell'elenco filtrato e
 * scegliere una voce — senza, la ricerca filtra ma rende l'elenco inutilizzabile senza mouse.
 * Il resto resta fermo qui: i tasti a un carattere sono quelli che innescano il typeahead nativo
 * del MenuList (stessa condizione, `key.length === 1`, che usa MUI in `MenuList.js`), e Home/End
 * dentro un input hanno un significato nativo di editing (inizio/fine riga) che confliggerebbe col
 * roving-tabindex del menu se li lasciassimo salire.
 */
const SEARCH_INPUT_BUBBLE_KEYS = ['Escape', 'ArrowUp', 'ArrowDown'];

function handleSearchInputKeyDown(e: KeyboardEvent<HTMLInputElement>) {
  if (!SEARCH_INPUT_BUBBLE_KEYS.includes(e.key)) {
    e.stopPropagation();
  }
}

/**
 * Combo del builder resa come chip `builder-selchip` (estetica del mockup) che apre un Menu MUI
 * ancorato al chip. `disabled` la rende inerte (es. la combo evento finché non è stato scelto un
 * oggetto).
 *
 * La usano tutti e tre i blocchi: oggetto ed evento nel QUANDO, campo/operatore/sorgente/valore nel
 * SE, campo e politica di errore nell'ALLORA.
 */
export function ChipSelect({
  value,
  options,
  placeholder,
  ariaLabel,
  onChange,
  disabled = false,
  className = 'builder-selchip',
  style,
}: {
  value: string;
  options: ChipOption[];
  /** Testo del chip finché `value` non è tra le `options`. Omissibile solo se `value` è sempre
   *  valorizzato (es. la combo sorgente del SE, che nasce già su 'STATIC'). */
  placeholder?: string;
  /**
   * Nome accessibile del chip. Senza, il bottone si annuncia col solo valore corrente ("Ritenta"),
   * che dice COSA è stato scelto ma non DI COSA — e un'etichetta visibile accanto non basta,
   * perché nulla la lega al controllo. Serve quindi dove il chip ha una label a fianco o un
   * placeholder che sparisce alla prima scelta; nel SE, dove la riga si legge come una frase, non
   * aggiunge nulla.
   */
  ariaLabel?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Classe del chip: 'builder-selchip' (default, campo/valore) o 'builder-op' (connettori della
   *  riga condizione: operatore e sorgente del valore). */
  className?: string;
  /** Stile inline sul chip (es. colori della categoria: backColor/fontColor). */
  style?: CSSProperties;
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [filter, setFilter] = useState('');
  const selected = options.find((o) => o.value === value);
  const showSearch = options.length > SEARCH_THRESHOLD;
  const normalizedFilter = normalizeForSearch(filter);
  const visibleOptions = showSearch
    ? options.filter((o) => normalizeForSearch(o.label).includes(normalizedFilter))
    : options;
  // Azzerato alla chiusura, non alla digitazione: riaprendo la tendina si riparte sempre
  // dall'elenco intero, non da quanto si era digitato la volta precedente.
  const closeMenu = () => {
    setAnchorEl(null);
    setFilter('');
  };

  return (
    <>
      <button
        type="button"
        className={className}
        style={style}
        disabled={disabled}
        aria-label={ariaLabel}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {selected ? selected.label : placeholder} ▾
      </button>
      {/* `autoFocus`/`disableAutoFocus` solo quando c'è una ricerca: senza, il Menu terrebbe il
          comportamento di default (focus sulla voce selezionata), che va bene per le liste corte. Con
          la ricerca, il Menu non deve rubare il fuoco (né il MenuList né la Popover sottostante):
          lo prende l'input, con l'`autoFocus` HTML nativo qui sotto. `disableAutoFocus` va passato
          SOLO quando serve: esplicitarlo a `false` sotto soglia riattiverebbe un meccanismo che
          MUI disabilita di proposito (vedi il commento in `Menu.js`: quando il Menu gestisce da sé
          il fuoco, la Popover sottostante deve restare fuori, altrimenti si contendono il fuoco). */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        autoFocus={!showSearch}
        {...(showSearch ? { disableAutoFocus: true } : {})}
      >
        {showSearch && (
          // `role="none"` perché l'unico figlio ammesso da un <ul role="menu"> è una voce
          // (menuitem/menuitemcheckbox/menuitemradio/group): togliamo il ruolo generico di
          // `<li>`, l'input tiene il proprio ruolo nativo di campo di testo perché gli elementi
          // con semantica nativa forte non erediscono `role="none"` dall'antenato.
          <li className="builder-searchbox" role="none">
            <input
              type="text"
              autoFocus
              value={filter}
              aria-label={t('automation.form.builder.searchPlaceholder')}
              placeholder={t('automation.form.builder.searchPlaceholder')}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={handleSearchInputKeyDown}
            />
          </li>
        )}
        {visibleOptions.map((o) => (
          <MenuItem
            key={o.value}
            selected={o.value === value}
            onClick={() => {
              onChange(o.value);
              closeMenu();
            }}
          >
            {o.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * Variante multi-selezione di {@link ChipSelect}: il menu NON si chiude al click e gli item sono
 * spuntabili; il chip mostra le etichette selezionate (o il placeholder se vuoto). La usa il blocco
 * SE per gli operatori a cardinalità SET (es. IN), dove il valore è un insieme.
 */
function MultiChipSelect({
  values,
  options,
  placeholder,
  onChange,
  className = 'builder-selchip builder-selchip--val',
}: {
  values: string[];
  options: ChipOption[];
  placeholder: string;
  onChange: (values: string[]) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [filter, setFilter] = useState('');
  const selectedLabels = options.filter((o) => values.includes(o.value)).map((o) => o.label);
  const toggle = (value: string) =>
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);
  const showSearch = options.length > SEARCH_THRESHOLD;
  const normalizedFilter = normalizeForSearch(filter);
  // Le voci già selezionate restano visibili anche quando il filtro le escluderebbe, al loro
  // posto nell'ordine (non spostate in testa): altrimenti l'utente che ha selezionato una voce e
  // poi digita qualcosa che non la contiene perde il modo di deselezionarla.
  const visibleOptions = showSearch
    ? options.filter((o) => values.includes(o.value) || normalizeForSearch(o.label).includes(normalizedFilter))
    : options;
  const closeMenu = () => {
    setAnchorEl(null);
    setFilter('');
  };

  return (
    <>
      <button type="button" className={className} onClick={(e) => setAnchorEl(e.currentTarget)}>
        {selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder} ▾
      </button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        autoFocus={!showSearch}
        {...(showSearch ? { disableAutoFocus: true } : {})}
      >
        {showSearch && (
          <li className="builder-searchbox" role="none">
            <input
              type="text"
              autoFocus
              value={filter}
              aria-label={t('automation.form.builder.searchPlaceholder')}
              placeholder={t('automation.form.builder.searchPlaceholder')}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={handleSearchInputKeyDown}
            />
          </li>
        )}
        {visibleOptions.map((o) => (
          <MenuItem
            key={o.value}
            selected={values.includes(o.value)}
            onClick={() => toggle(o.value)}
          >
            <Checkbox size="small" checked={values.includes(o.value)} sx={{ p: 0, mr: 1 }} />
            {o.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * Editor del valore di un campo del catalogo. La forma dipende da DUE cose ortogonali: la
 * `cardinality` decide QUANTI slot (SINGLE = uno, RANGE = coppia from/to, SET = insieme, RELATIVE =
 * quantità e unità), il `type` del {@link CatalogField} decide il WIDGET di ogni slot (ENUM →
 * tendina, BOOLEAN → switch, DATE → date, numerici → number, STRING → text). I valori multi-slot
 * sono serializzati come array JSON nel singolo campo `value` (`["from","to"]` / `["v1","v2"]` /
 * `["30","DAY"]`).
 *
 * La cardinalità la porta l'OPERATORE, quindi solo il blocco SE usa RANGE e SET. Chi non ha
 * operatori — la configurazione di un'azione, che assegna e non confronta — passa `SINGLE` e ottiene
 * il solo widget del tipo. Il valore resta una STRINGA in entrambi i casi: la tipizzazione è del
 * chiamante, che è l'unico a sapere dove quel valore andrà a finire.
 */
export function CatalogValueField({
  field,
  cardinality,
  value,
  onChange,
}: {
  field: CatalogField;
  cardinality: OperatorCardinality;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();

  // SET (es. IN): multi-selezione dei valori enum; valore = array JSON dei value selezionati.
  if (cardinality === 'SET') {
    const selected = parseJsonArray(value);
    return (
      <MultiChipSelect
        values={selected}
        options={field.enumValues.map((ev) => ({ value: ev.value, label: ev.label }))}
        placeholder={t('automation.form.builder.selectValue')}
        onChange={(vals) => onChange(vals.length > 0 ? JSON.stringify(vals) : '')}
      />
    );
  }

  // RELATIVE (es. «entro i prossimi», solo sulle date): non una data ma una quantità intera e la sua
  // unità; valore = ["30","DAY"]. Il widget non è quello del tipo del campo, che qui non conta.
  // L'unità scelta sopravvive anche senza quantità, così chi la cambia prima di scrivere il numero
  // non la perde; con la sola unità di default il valore resta vuoto, come una riga da compilare.
  if (cardinality === 'RELATIVE') {
    const [amount = '', unit = DEFAULT_RELATIVE_UNIT] = parseJsonArray(value);
    const setRelative = (nextAmount: string, nextUnit: string) =>
      onChange(
        nextAmount || nextUnit !== DEFAULT_RELATIVE_UNIT
          ? JSON.stringify([nextAmount, nextUnit])
          : '',
      );
    return (
      <span className="builder-fieldnum">
        <input
          type="number"
          min="0"
          step="1"
          value={amount}
          className="builder-valinput--num"
          aria-label={field.label}
          onChange={(e) => setRelative(e.target.value, unit)}
        />{' '}
        <ChipSelect
          className="builder-selchip builder-selchip--val"
          value={unit}
          options={RELATIVE_UNITS.map((u) => ({
            value: u,
            label: t(`automation.form.builder.relativeUnits.${u}`),
          }))}
          onChange={(nextUnit) => setRelative(amount, nextUnit)}
        />
      </span>
    );
  }

  // SINGLE su ENUM: tendina a selezione singola.
  if (field.type === 'ENUM') {
    return (
      <ChipSelect
        className="builder-selchip builder-selchip--val"
        value={value}
        options={field.enumValues.map((ev) => ({ value: ev.value, label: ev.label }))}
        placeholder={t('automation.form.builder.selectValue')}
        onChange={onChange}
      />
    );
  }

  // BOOLEAN: switch (ammette solo EQ, quindi sempre SINGLE).
  if (field.type === 'BOOLEAN') {
    return (
      <span className="builder-fieldnum">
        <Switch
          size="small"
          checked={value === 'true'}
          onChange={(e) => onChange(String(e.target.checked))}
        />
      </span>
    );
  }

  // Slot scalare (numerici/DATE/STRING): input HTML per-tipo, riusato dai due estremi del RANGE.
  const inputType = field.type === 'DATE' ? 'date' : field.type === 'STRING' ? 'text' : 'number';
  const step =
    field.type === 'INTEGER' ? '1' : field.type === 'DECIMAL' || field.type === 'MONEY' ? '0.01' : undefined;
  // Larghezza per CLASSE e non con uno style inline: la regola del progetto è che nessuno stile
  // visivo stia nel JSX, e qui la larghezza dipende da un insieme chiuso di tipi — tre casi, tre
  // modificatori, nessun valore calcolato da passare.
  const widthClass =
    field.type === 'DATE'
      ? 'builder-valinput--date'
      : field.type === 'STRING'
        ? 'builder-valinput--text'
        : 'builder-valinput--num';
  const suffix = field.type === 'MONEY' ? ' €' : '';
  const scalarInput = (v: string, set: (s: string) => void, aria: string) => (
    <input
      type={inputType}
      step={step}
      value={v}
      className={widthClass}
      aria-label={aria}
      onChange={(e) => set(e.target.value)}
    />
  );

  // RANGE (es. BETWEEN): coppia from/to dello stesso tipo; valore = ["from","to"].
  if (cardinality === 'RANGE') {
    const [from = '', to = ''] = parseJsonArray(value);
    const fromLabel = t('automation.form.builder.rangeFrom');
    const toLabel = t('automation.form.builder.rangeTo');
    const setPair = (nextFrom: string, nextTo: string) =>
      onChange(nextFrom || nextTo ? JSON.stringify([nextFrom, nextTo]) : '');
    return (
      <span className="builder-fieldnum">
        {fromLabel}{' '}
        {scalarInput(from, (s) => setPair(s, to), `${field.label} ${fromLabel}`)} {toLabel}{' '}
        {scalarInput(to, (s) => setPair(from, s), `${field.label} ${toLabel}`)}
        {suffix}
      </span>
    );
  }

  // SINGLE scalare.
  return (
    <span className="builder-fieldnum">
      {scalarInput(value, onChange, field.label)}
      {suffix}
    </span>
  );
}
